/**
 * Cafeteria serve-sheet print plan — the pure data layer behind the Serve
 * meals Print feature. Builds a `ServePrintTarget` (one or all mealtimes)
 * from the live service-session data + the page's uncommitted local serve
 * state, and enumerates the print blocks for the shared page-plan engine
 * (`@/lib/print/page-plan`).
 *
 * The printed sheet is a paper serving WORKSHEET (the sanctioned offline
 * fallback is a paper log): student/tally rows carry blank hand-write boxes
 * (roster boxes pre-check students already marked served on screen). Print
 * ignores the Serve page's search/sort/chip filters. The school print
 * dialog can explicitly request a subset of the roster's groups.
 *
 * Side-effect free; unit-tested in `client/test/lib/cafeteria/print-plan.test.ts`.
 */

import type {
  CafeteriaServiceSessionResponse,
  Mealtime,
  ServeDeliverableRowWire,
  ServeGroupDimension,
  ServeMode,
  ServeSessionStatus,
} from '@/api/cafeteria'
import {
  deliverableLines,
  deliverableRowKey,
  formatMealLineLabel,
  formatServiceDateLabel,
  formatShortServiceDateLabel,
  groupDeliverableRows,
  groupStudentRows,
  MEALTIME_LABELS,
  type ServePortionCaptionPart,
  servePortionCaptionParts,
  serviceDateToDate,
  type StudentMealGroup,
} from '@/lib/cafeteria/format'
import { bucketForStudentGroup } from '@/lib/cafeteria/serve-buckets'
import {
  axisForDimension,
  gradeAxisForStudentGroup,
  homeroomAxisForStudentGroup,
  homeroomFacetLabel,
} from '@/lib/cafeteria/serve-filters'
import { formatShortDayRange } from '@/lib/chefs/format'
import type { PagePlanStructure, TocEntry } from '@/lib/print/page-plan'

export type ServePrintScope = 'mealtime' | 'day'

/** The page's uncommitted local serve state (see serve.tsx / the draft store). */
export interface ServeLocalOverrides {
  /** Student-group toggle overrides, keyed by `StudentMealGroup.key`. */
  servedOverrides: Record<string, boolean>
  /** Tally stepper counts, keyed by `deliverableRowKey(row)`. */
  quantityServed: Record<string, number>
}

export interface ServePrintRosterRow {
  key: string
  studentName: string
  /** Grade or homeroom — the same bucket the serve chips group by. */
  bucket: string | null
  /** Formatted meal lines, identical to the on-screen serve card. */
  mealLines: string[]
  /** Live served state at print time (local toggle folded over server state). */
  served: boolean
}

export interface ServePrintRosterGroup {
  /** Bucket heading; null renders a heading-less group (no buckets at all). */
  label: string | null
  students: ServePrintRosterRow[]
}

export interface ServePrintTallyRow {
  key: string
  label: string
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  /** Labeled serving size + utensil caption parts; empty when unauthored. */
  captionParts: ServePortionCaptionPart[]
  planned: number
}

export interface ServePrintTallyGroup {
  label: string
  rows: ServePrintTallyRow[]
}

export interface ServePrintMealtimeData {
  mealtime: Mealtime
  label: string
  /** Service window clock labels, e.g. "12:00 PM". */
  serviceStarts: string
  serviceEnds: string
  status: ServeSessionStatus
  serveMode: ServeMode
  instructions: string | null
  rosterGroups: ServePrintRosterGroup[]
  tallyGroups: ServePrintTallyGroup[]
  /** Serve units for this mealtime (students + forecast servings). */
  totalUnits: number
}

export interface ServePrintDay {
  /** ISO yyyy-mm-dd service date. */
  serviceDate: string
  /** Long date label for the page header band, e.g. "Monday, July 27". */
  serviceDateLabel: string
  /** Compact label for pills / manifest rows, e.g. "Mon 07/27". */
  shortDateLabel: string
  /**
   * False ⇒ this day's ordering window is still open, so counts can move
   * between now and service. Drives the printed warning band. See
   * `@/lib/cafeteria/order-window`.
   */
  ordersConfirmed: boolean
  /** Chronological; EMPTY-mode mealtimes are excluded before building. */
  mealtimes: ServePrintMealtimeData[]
}

/**
 * How the printed roster arranges students.
 *
 * - 'grade'    — one table per grade (PROD-1234). The grade axis is the one
 *                the serve filters use: the campus bucket when the campus
 *                groups by grade, else the student's grade display field.
 * - 'homeroom' — one table per homeroom (PROD-1234), mirror of the above on
 *                the homeroom axis. A campus with both grades and homerooms
 *                (St. Hugo) can now print per homeroom even though its serve
 *                policy groups by grade.
 * - 'alpha'    — one flat A–Z list across the whole campus, each row carrying
 *                its bucket inline.
 * - 'buckets'  — LEGACY: one table per campus serve bucket, whatever axis the
 *                policy names. Kept so pre-PROD-1234 print links (no
 *                `grouping` param) keep printing what they printed; the dialog
 *                no longer emits it.
 *
 * Sheila's original spec named two: "per grouping (grade/homeroom) or all
 * students in a list"; Mark's follow-up split the first into its two axes.
 */
export type RosterGrouping = 'buckets' | 'grade' | 'homeroom' | 'alpha'

/** Student groups selected in the school print dialog, independently of arrangement. */
export interface RosterFilter {
  axis: 'grade' | 'homeroom'
  labels: ReadonlySet<string>
}

export function rosterFilterLabel(
  group: StudentMealGroup,
  axis: RosterFilter['axis'],
  serveGroupBy: ServeGroupDimension | null | undefined,
): string {
  return rosterBucketForGrouping(group, axis, serveGroupBy) ?? 'Other'
}

/** `?grouping=` → RosterGrouping; anything unrecognised is the legacy default. */
export function parseRosterGroupingParam(value: string | null | undefined): RosterGrouping {
  return value === 'grade' || value === 'homeroom' || value === 'alpha' ? value : 'buckets'
}

/**
 * The pill the dialog starts on: the axis the campus serve policy already
 * groups by, so the default printout is what "Per grade / homeroom" printed
 * before the split. A homeroom-axis campus (HOMEROOM / TEACHER / ROOM /
 * LUNCH_PERIOD) starts on its own axis; everything else starts on grade.
 */
export function defaultRosterGrouping(serveGroupBy: ServeGroupDimension | null | undefined): RosterGrouping {
  return axisForDimension(serveGroupBy) === 'homeroom' ? 'homeroom' : 'grade'
}

export interface RosterGroupingOption {
  value: RosterGrouping
  label: string
}

/**
 * The grouping pills the print dialog offers. "Per homeroom" is offered only
 * where it can bucket anything — a campus with homeroom Enrollment facts, or
 * one whose serve policy already sits on the homeroom axis (its buckets ARE
 * the option). Offering it elsewhere would print one heading-less list and
 * call it "per homeroom". The pill is titled from the campus dimension so a
 * room-grouped campus reads "Per classroom", matching the serve filter bar.
 */
export function rosterGroupingOptions(campus: {
  serveGroupBy: ServeGroupDimension | null | undefined
  hasHomerooms: boolean
}): RosterGroupingOption[] {
  const options: RosterGroupingOption[] = [{ value: 'grade', label: 'Per grade' }]
  if (campus.hasHomerooms || axisForDimension(campus.serveGroupBy) === 'homeroom') {
    options.push({ value: 'homeroom', label: `Per ${homeroomFacetLabel(campus.serveGroupBy).toLowerCase()}` })
  }
  options.push({ value: 'alpha', label: 'All students A–Z' })
  return options
}

/**
 * Roster heading for staff self-diners (PROD-1439) — a teacher ordering their
 * own lunch has no grade or homeroom, and filing them under "Other" read as an
 * unplaced student. Same word as the Orders sheet's catch column.
 */
export const STAFF_ROSTER_LABEL = 'Staff'

/**
 * The heading a student files under for a grouping. 'alpha' rows keep the
 * campus bucket for inline display; 'buckets' is the legacy campus bucket.
 * Staff self-diners file under "Staff" on every axis.
 */
export function rosterBucketForGrouping(
  group: StudentMealGroup,
  grouping: RosterGrouping,
  serveGroupBy: ServeGroupDimension | null | undefined,
): string | null {
  if (group.isStaffDiner) {
    return STAFF_ROSTER_LABEL
  }
  switch (grouping) {
    case 'grade':
      return gradeAxisForStudentGroup(group, serveGroupBy)
    case 'homeroom':
      return homeroomAxisForStudentGroup(group, serveGroupBy)
    default:
      return bucketForStudentGroup(group)
  }
}

export interface ServePrintTarget {
  scope: ServePrintScope
  rosterGrouping: RosterGrouping
  schoolName: string
  /**
   * Chronological; days with nothing printable are dropped before building.
   *
   * A multi-day packet is N SELF-CONTAINED daily sheets stapled together — it
   * is deliberately NOT the chefs model, where the ingredient list aggregates
   * across days (`chefs/print/print-root.tsx`). Every quantity on paper stays
   * scoped to one day and one mealtime, because that is the unit someone
   * actually serves from. Do not add a cross-day total anywhere.
   */
  days: ServePrintDay[]
  /** Header/cover label — one long day label, or "Mon 07/27 – Fri 07/31". */
  rangeLabel: string
  /** Wall-clock stamp of when this packet was generated. */
  printedAtLabel: string
}

// ---------------------------------------------------------------------------
// Block structure — shared ids between the renderer (data-print-block tags)
// and the plan, so measured heights line up with plan blocks.
// ---------------------------------------------------------------------------

export const SERVE_COVER_BLOCK_ID = 'serve-cover'
export const serveMealtimeBlockId = (serviceDate: string, mealtime: Mealtime): string =>
  `serve-mealtime|${serviceDate}|${mealtime}`

/** Every printed mealtime across the packet, in page order. */
function allMealtimes(target: ServePrintTarget): Array<{ day: ServePrintDay; mealtime: ServePrintMealtimeData }> {
  return target.days.flatMap((day) => day.mealtimes.map((mealtime) => ({ day, mealtime })))
}

/**
 * A cover page (with the contents manifest) earns its paper as soon as the
 * packet holds more than one mealtime section — whether that's one day's
 * breakfast + lunch, or a week of lunches. A lone section is self-describing.
 */
export function serveCoverIncluded(target: ServePrintTarget): boolean {
  return allMealtimes(target).length > 1
}

export function buildServePrintStructure(target: ServePrintTarget): PagePlanStructure {
  const withCover = serveCoverIncluded(target)
  const sections = allMealtimes(target)
  const blocks = [
    ...(withCover ? [{ id: SERVE_COVER_BLOCK_ID }] : []),
    ...sections.map(({ day, mealtime }) => ({ id: serveMealtimeBlockId(day.serviceDate, mealtime.mealtime) })),
  ]
  // TOC entries only exist alongside the cover that displays them. Across days
  // the mealtime label alone is ambiguous ("Lunch" five times), so it's
  // qualified by day.
  const multiDay = target.days.length > 1
  const toc = withCover
    ? sections.map(({ day, mealtime }) => ({
        label: multiDay ? `${day.shortDateLabel} — ${mealtime.label}` : mealtime.label,
        firstBlockId: serveMealtimeBlockId(day.serviceDate, mealtime.mealtime),
      }))
    : []
  return { blocks, toc }
}

/**
 * Stable identity of a target's LAYOUT — the measurement cache key. Row and
 * group counts (plus per-row meal-line counts) are what move block heights;
 * digit-only changes (a tally tick) don't re-measure. `ordersConfirmed` is in
 * the key because flipping it renders/removes the warning band.
 */
export function servePlanSignature(target: ServePrintTarget): string {
  const days = target.days.map((day) => {
    const parts = day.mealtimes.map((m) => {
      const rosterRows = m.rosterGroups.reduce((n, g) => n + g.students.length, 0)
      const mealLines = m.rosterGroups.reduce((n, g) => n + g.students.reduce((s, r) => s + r.mealLines.length, 0), 0)
      const tallyRows = m.tallyGroups.reduce((n, g) => n + g.rows.length, 0)
      // Captions add a second line to their rows, so their count moves height.
      const tallyCaptions = m.tallyGroups.reduce(
        (n, g) => n + g.rows.filter((r) => r.captionParts.length > 0).length,
        0,
      )
      return `${m.mealtime}:${m.serveMode}:rg${m.rosterGroups.length}:r${rosterRows}:ml${mealLines}:tg${m.tallyGroups.length}:t${tallyRows}:tc${tallyCaptions}:i${m.instructions ? 1 : 0}`
    })
    return `${day.serviceDate}:c${day.ordersConfirmed ? 1 : 0}:${parts.join('~')}`
  })
  // Grouping is in the key: flipping buckets↔alpha reshapes every roster
  // table and must re-measure.
  return `${target.scope}|g:${target.rosterGrouping}|${days.join('|')}`
}

/**
 * Suggested filename for the browser's save-as-PDF dialog (set as
 * `document.title` while printing). Mirrors the CSV button's
 * `serve-<date>-<mealtime>` naming, lowercased, widening to a
 * `<first>-to-<last>` range across days.
 */
export function servePrintDocumentTitle(target: ServePrintTarget, mealtime: Mealtime): string {
  const isos = target.days.map((d) => d.serviceDate)
  const span = isos.length > 1 ? `${isos[0]}-to-${isos[isos.length - 1]}` : (isos[0] ?? 'serve')
  return target.scope === 'day' ? `serve-${span}-all-mealtimes` : `serve-${span}-${mealtime.toLowerCase()}`
}

/** Header/cover label for the packet's date span. */
export function servePrintRangeLabel(serviceDates: readonly string[]): string {
  if (serviceDates.length === 0) {
    return ''
  }
  if (serviceDates.length === 1) {
    return formatServiceDateLabel(serviceDates[0]!)
  }
  const first = serviceDateToDate(serviceDates[0]!)
  const last = serviceDateToDate(serviceDates[serviceDates.length - 1]!)
  return first && last
    ? formatShortDayRange(first, last)
    : `${serviceDates[0]} – ${serviceDates[serviceDates.length - 1]}`
}

/**
 * "Printed" stamp for the page headers. A week packet is printed days ahead of
 * service and reprinted when counts move, so the paper has to say when it was
 * generated — otherwise two versions of Thursday are indistinguishable.
 */
export function servePrintedAtLabel(now: Date = new Date()): string {
  return now.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// ---------------------------------------------------------------------------
// Unconfirmed-orders warnings — one source of wording for the print dialog
// and the printed page.
// ---------------------------------------------------------------------------

/** Printed band on any day whose ordering window is still open. */
export const SERVE_UNCONFIRMED_NOTE =
  'Orders for this day are not final — counts may still change before service. Reprint to pick up changes.'

/** Comma-and list: "Thu 07/30", "Thu 07/30 and Fri 07/31", "A, B, and C". */
function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) {
    return labels[0] ?? ''
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

/**
 * The print dialog's warning under the day selector, or null when every
 * selected day's orders are already final.
 */
export function unconfirmedDaysWarning(
  days: ReadonlyArray<Pick<ServePrintDay, 'shortDateLabel' | 'ordersConfirmed'>>,
): string | null {
  const open = days.filter((d) => !d.ordersConfirmed).map((d) => d.shortDateLabel)
  if (open.length === 0) {
    return null
  }
  return `Ordering is still open for ${joinLabels(open)} — counts may change before service. Reprint to pick up changes.`
}

// ---------------------------------------------------------------------------
// Cover manifest — what's in this stack, per day. Never a cross-day total.
// ---------------------------------------------------------------------------

export interface ServePrintManifestRow {
  serviceDate: string
  dayLabel: string
  /** This day's mealtimes, e.g. "Lunch" or "Breakfast, Lunch". */
  mealtimeLabel: string
  /** THIS DAY's serve units — deliberately never summed across days. */
  mealCount: number
  ordersConfirmed: boolean
  /** Page the day's first section starts on. */
  page: number
}

/**
 * One manifest row per day, pairing each day with its first section's measured
 * page. `toc` must be the resolved TOC for the same target — its entries are
 * generated one-per-mealtime in the same order by `buildServePrintStructure`,
 * which is the coupling this function exists to contain.
 */
export function buildServePrintManifest(target: ServePrintTarget, toc: readonly TocEntry[]): ServePrintManifestRow[] {
  const rows: ServePrintManifestRow[] = []
  let cursor = 0
  for (const day of target.days) {
    const page = toc[cursor]?.page ?? 0
    cursor += day.mealtimes.length
    rows.push({
      serviceDate: day.serviceDate,
      dayLabel: day.serviceDateLabel,
      mealtimeLabel: day.mealtimes.map((m) => m.label).join(', '),
      mealCount: day.mealtimes.reduce((sum, m) => sum + m.totalUnits, 0),
      ordersConfirmed: day.ordersConfirmed,
      page,
    })
  }
  return rows
}

// ---------------------------------------------------------------------------
// Data assembly
// ---------------------------------------------------------------------------

/**
 * Bucket the (already name-sorted) student groups for the printed roster —
 * by grade, by homeroom, or by the legacy campus bucket (see
 * `RosterGrouping`). All-null buckets collapse into one heading-less group;
 * group order is numeric-aware ("Grade 9" before "Grade 10"), then staff
 * self-diners ("Staff"), with the unbucketed group last (it prints as "Other"
 * when headings show).
 *
 * `serveGroupBy` is the campus policy the session's `groupLabel`s were
 * resolved under; the grade/homeroom axes need it to know which field the
 * rendered bucket belongs to (`serve-filters.ts`).
 */
export function groupRosterForPrint(
  groups: ReadonlyArray<StudentMealGroup>,
  isServedLive: (group: StudentMealGroup) => boolean,
  grouping: RosterGrouping = 'buckets',
  serveGroupBy: ServeGroupDimension | null | undefined = null,
  filter?: RosterFilter,
): ServePrintRosterGroup[] {
  const selected = filter
    ? groups.filter((group) => filter.labels.has(rosterFilterLabel(group, filter.axis, serveGroupBy)))
    : groups
  const rows: ServePrintRosterRow[] = selected.map((group) => ({
    key: group.key,
    studentName: group.studentName,
    bucket: rosterBucketForGrouping(group, grouping === 'alpha' && filter ? filter.axis : grouping, serveGroupBy),
    mealLines: deliverableLines(group).map((line) => formatMealLineLabel(line.label, line.portion, line.quantity)),
    served: isServedLive(group),
  }))
  if (grouping === 'alpha') {
    // One flat list, preserving the input's name sort. Rows keep `bucket`
    // for inline display; students with no enrollment simply have none.
    return rows.length > 0 ? [{ label: null, students: rows }] : []
  }
  const byBucket = new Map<string | null, ServePrintRosterRow[]>()
  for (const row of rows) {
    const list = byBucket.get(row.bucket) ?? []
    list.push(row)
    byBucket.set(row.bucket, list)
  }
  return Array.from(byBucket.entries())
    .map(([label, students]) => ({ label, students }))
    .sort(
      (a, b) =>
        rosterGroupRank(a.label) - rosterGroupRank(b.label) ||
        (a.label ?? '').localeCompare(b.label ?? '', undefined, { numeric: true }),
    )
}

/** Campus buckets first, then "Staff", then the unbucketed group. */
function rosterGroupRank(label: string | null): number {
  if (label === null) {
    return 2
  }
  return label === STAFF_ROSTER_LABEL ? 1 : 0
}

function buildTallyGroups(rows: ReadonlyArray<ServeDeliverableRowWire>): ServePrintTallyGroup[] {
  return groupDeliverableRows(rows).map((group) => ({
    label: group.label,
    rows: group.rows.map((row) => ({
      key: deliverableRowKey(row),
      label: row.label,
      temperatureClass: row.temperatureClass,
      captionParts: servePortionCaptionParts(row),
      planned: row.planned,
    })),
  }))
}

/**
 * Assemble one mealtime's print data from its full session detail + the
 * page's local overrides. Reads the full session before applying an explicit
 * print-dialog roster filter; the Serve page's search filters never leak in.
 */
export function buildServePrintMealtime(
  session: CafeteriaServiceSessionResponse,
  overrides: ServeLocalOverrides,
  grouping: RosterGrouping = 'buckets',
  rosterFilter?: RosterFilter,
): ServePrintMealtimeData {
  // The roster is built from PARENT_ORDER items ONLY — the privacy boundary.
  // A school-forecast (anonymous) row must never surface as a named student,
  // in either grouping mode; grouping happens strictly downstream of here.
  const parentItems = session.items.filter((item) => item.source === 'PARENT_ORDER')
  const studentGroups = groupStudentRows(parentItems)
  const isServedLive = (group: StudentMealGroup): boolean =>
    overrides.servedOverrides[group.key] ?? (group.total > 0 && group.remaining === 0)

  return {
    mealtime: session.mealtime,
    label: MEALTIME_LABELS[session.mealtime],
    serviceStarts: session.summary.serviceStarts,
    serviceEnds: session.summary.serviceEnds,
    status: session.summary.status,
    serveMode: session.serveMode,
    instructions: session.instructions,
    rosterGroups: groupRosterForPrint(
      studentGroups,
      isServedLive,
      grouping,
      session.location.groupingPolicy?.serveGroupBy ?? null,
      rosterFilter,
    ),
    tallyGroups: buildTallyGroups(session.deliverableRows),
    totalUnits: session.summary.totalItems,
  }
}

/**
 * Assemble one day of the packet from its (already chronologically ordered)
 * session details. Returns null when the day has nothing printable, so callers
 * can drop it from `days` rather than printing an empty sheet.
 *
 * `overridesFor` MUST resolve THIS day's serve drafts — the draft store is
 * keyed `(locationId, serviceDate, mealtime)`, so reusing the active day's
 * overrides across a multi-day packet would stamp today's served checkmarks
 * onto every other day's roster, and reusing one mealtime's overrides across
 * the day would stamp breakfast's checkmarks onto the lunch roster (the
 * student toggle keys are student-scoped).
 */
export function buildServePrintDay(
  serviceDate: string,
  sessions: ReadonlyArray<CafeteriaServiceSessionResponse>,
  overridesFor: (mealtime: CafeteriaServiceSessionResponse['mealtime']) => ServeLocalOverrides,
  ordersConfirmed: boolean,
  grouping: RosterGrouping = 'buckets',
  rosterFilter?: RosterFilter,
): ServePrintDay | null {
  const mealtimes = sessions
    .filter((session) => session.serveMode !== 'EMPTY')
    .map((session) => buildServePrintMealtime(session, overridesFor(session.mealtime), grouping, rosterFilter))
    .filter((mealtime) => !rosterFilter || mealtime.rosterGroups.length > 0 || mealtime.tallyGroups.length > 0)
  if (mealtimes.length === 0) {
    return null
  }
  return {
    serviceDate,
    serviceDateLabel: formatServiceDateLabel(serviceDate),
    shortDateLabel: formatShortServiceDateLabel(serviceDate),
    ordersConfirmed,
    mealtimes,
  }
}
